<template>
  <svg
    :viewBox="`0 0 ${svgWidth} 220`"
    style="width:100%;display:block;overflow:visible;"
    :aria-label="mode === 'metrics' ? `Response performance snapshot for ${violins.length} metrics` : `VivaScore comparison across ${violins.length} assignments`"
  >
    <!-- Y-axis -->
    <line x1="32" y1="10" x2="32" y2="170" stroke="#E8E4DC" stroke-width="0.5" />
    <g v-for="z in yAxisLabels" :key="z">
      <line
        x1="28" :y1="dotY(z)"
        x2="36" :y2="dotY(z)"
        stroke="#D0CCC4" stroke-width="0.5"
      />
      <text x="26" :y="dotY(z) + 3" font-size="8" fill="#B4B0A8" text-anchor="end">
        {{ z === 0 ? 'avg' : z > 0 ? `+${z}` : z }}
      </text>
    </g>

    <!-- One violin per metric (Mode A: 5 metrics side-by-side, Review.vue) or
         per assignment (Mode B: N assignments over time, StudentSubmissions.vue) -->
    <g v-for="(violin, i) in violins" :key="violin.label">

      <!-- Violin shape: kernel density estimate of peer z-scores.
           High bandwidth (0.7) keeps the shape smooth and non-dramatic for teachers.
           A data-scientist audience might prefer 0.3–0.4 for more detail. -->
      <path
        :d="violinPath(violin.peers, cx(i))"
        fill="#EDEAE4"
        stroke="#D8D4CC"
        stroke-width="0.5"
        opacity="0.8"
      />

      <!-- Mode A only: dashed reference line at z=0 (the overall class average) -->
      <line
        v-if="mode === 'metrics'"
        :x1="cx(i) - 24" :y1="yCenter"
        :x2="cx(i) + 24" :y2="yCenter"
        stroke="#C8C4BC" stroke-width="0.8" stroke-dasharray="3,2"
      />

      <!-- Mode A: jittered anonymous peer dots + single highlighted student dot -->
      <g v-if="mode === 'metrics'">
        <circle
          v-for="(peerZ, j) in violin.peers"
          :key="j"
          :cx="cx(i) + jitter[j % jitter.length]"
          :cy="dotY(peerZ)"
          r="2.8"
          :style="{ fill: colors.peers }"
          opacity="0.85"
        />
        <!-- Student dot — omitted if studentZ is null (score unavailable) -->
        <!-- Uses 'below' color (orange) regardless of above/below avg;
             above/below distinction is handled by MetricBar (A5), not the violin. -->
        <circle
          v-if="violin.studentZ !== null"
          :cx="cx(i)"
          :cy="dotY(violin.studentZ)"
          r="6"
          :style="{ fill: colors.below }"
        />
      </g>

      <!-- Mode B: individual student dots — each in their legend color; larger when active.
           Render order: non-active dots → avg dot → active dots, so active students
           always appear on top and are never obscured by other dots. -->
      <g v-if="mode === 'assignments'">
        <!-- Pass 1: non-active student dots -->
        <circle
          v-for="student in violin.students.filter(s => activeStudentIds.indexOf(s.id) === -1)"
          :key="student.id"
          :cx="cx(i) + (studentJitterMap[student.id] || 0)"
          :cy="dotY(student.z)"
          r="2.8"
          :style="{ fill: studentColors[student.id] || colors.peers, cursor: 'pointer' }"
          @click="$emit('student-clicked', student.id)"
        />
        <!-- Pass 2: avg dot -->
        <circle
          v-if="violin.avgZ !== null && classAvgHighlighted"
          :cx="cx(i)"
          :cy="dotY(violin.avgZ)"
          r="5"
          fill="#222222"
          style="cursor: pointer"
          @click="$emit('avg-clicked')"
        />
        <!-- Pass 3: active student dots — rendered last so they sit on top of all other dots -->
        <circle
          v-for="student in violin.students.filter(s => activeStudentIds.indexOf(s.id) !== -1)"
          :key="'active-' + student.id"
          :cx="cx(i) + (studentJitterMap[student.id] || 0)"
          :cy="dotY(student.z)"
          r="5"
          :style="{ fill: studentColors[student.id] || colors.peers, cursor: 'pointer' }"
          @click="$emit('student-clicked', student.id)"
        />
      </g>

      <!-- Metric or assignment label; Mode B uses smaller font so "A1: Era of..." fits -->
      <text
        :x="cx(i)" y="210"
        :font-size="mode === 'assignments' ? '6' : '9.5'"
        fill="#6A6660"
        text-anchor="middle"
        font-family="-apple-system,sans-serif"
      >{{ violin.label }}</text>

    </g>

    <!-- Mode B: dashed line connecting class average markers; hidden when Class Avg toggled off -->
    <polyline
      v-if="mode === 'assignments' && avgPolylinePoints && classAvgHighlighted"
      :points="avgPolylinePoints"
      fill="none"
      stroke="#222222"
      stroke-width="1.5"
      stroke-dasharray="4,3"
    />

    <!-- Mode B: trend lines for active students only, each in their legend color -->
    <polyline
      v-for="line in studentPolylines"
      :key="line.id"
      :points="line.points"
      fill="none"
      :stroke="line.color"
      stroke-width="2"
    />

    <!-- Mode A (studentTrend): trend line through the student's dots across violins -->
    <polyline
      v-if="studentTrendPoints"
      :points="studentTrendPoints"
      fill="none"
      :stroke="colors.below"
      stroke-width="2"
    />

  </svg>
</template>

<script>
import { VIVASCORE_COLORS } from '@/utils/vivascore'

export default {
  name: 'VivaViolinChart',
  emits: ['student-clicked', 'avg-clicked'],

  props: {
    // Mode A (default): 5 metrics side by side — used in Review.vue
    metrics: {
      type: Array,
      default: () => [],
      // [{ label: String, studentZ: Number|null, peers: [Number] }]
    },
    // Mode B: one violin per assignment, VivaScore only — used in StudentSubmissions.vue
    assignments: {
      type: Array,
      default: () => [],
      // [{ label: String, students: [{ id: Number, name: String, z: Number|null }] }]
    },
    // 'metrics' (default) or 'assignments'
    mode: {
      type: String,
      default: 'metrics',
    },
    // Mode B only: IDs of students whose trend lines are currently shown
    activeStudentIds: {
      type: Array,
      default: () => [],
    },
    // Mode B only: whether the class average line is highlighted
    classAvgHighlighted: {
      type: Boolean,
      default: false,
    },
    // Mode B only: map of student_id → CSS color string for per-student dots and trend lines
    studentColors: {
      type: Object,
      default: () => ({}),
    },
    // Mode A only: connect the student's dots across violins with a trend line.
    // For when Mode A violins represent assignments over time (StudentProgress
    // multi-VIVA view) rather than side-by-side metrics (Review) — the anonymous
    // counterpart of Mode B's studentPolylines, needing no student ids.
    studentTrend: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      yCenter: 100,
      yScale: 22,         // pixels per z-score unit; reduced from 26 to fit ±3 range in same height
      colW: 112,          // column width per metric or assignment
      leftOffset: 44,     // left margin reserved for y-axis
      jitter: [-5, -3, 0, 2, 4, -2, 5, -4],
      yAxisLabels: [3, 2, 1, 0, -1, -2, -3],
      colors: VIVASCORE_COLORS,
    }
  },

  computed: {
    // Normalizes both modes into one shared shape so the template can reuse KDE/label rendering.
    // Mode A: metrics  → { label, peers, studentZ, students: [], avgZ: null }
    // Mode B: assignments → { label, peers (all student z-scores), studentZ: null, students, avgZ }
    violins() {
      if (this.mode === 'assignments') {
        return this.assignments.map(a => {
          const zs = a.students.map(s => s.z).filter(z => z !== null)
          const avgZ = zs.length > 0 ? zs.reduce((sum, z) => sum + z, 0) / zs.length : null
          return {
            label: a.label,
            peers: zs,
            students: a.students,
            studentZ: null,
            avgZ,
          }
        })
      }
      return this.metrics.map(m => ({
        label: m.label,
        peers: m.peers,
        students: [],
        studentZ: m.studentZ,
        avgZ: null,
      }))
    },

    svgWidth() {
      return this.leftOffset + this.violins.length * this.colW
    },

    // SVG points string connecting class average markers across assignments (Mode B)
    avgPolylinePoints() {
      return this.violins
        .map((v, i) => v.avgZ !== null ? `${this.cx(i)},${this.dotY(v.avgZ)}` : null)
        .filter(Boolean)
        .join(' ')
    },

    // Mode A (studentTrend prop): SVG points string through the student's dots.
    // Dots sit at violin centers, so the line runs through column centers; violins
    // where studentZ is null (score unavailable) are skipped. Empty string (no line)
    // unless enabled and 2+ points exist.
    studentTrendPoints() {
      if (this.mode !== 'metrics' || !this.studentTrend) return ''
      const pts = this.violins
        .map((v, i) => v.studentZ !== null ? `${this.cx(i)},${this.dotY(v.studentZ)}` : null)
        .filter(Boolean)
      return pts.length >= 2 ? pts.join(' ') : ''
    },

    // Stable jitter per student ID based on first assignment's student order.
    // Ensures dot x-position and polyline endpoint x-position stay in sync across assignments.
    studentJitterMap() {
      if (this.mode !== 'assignments' || this.assignments.length === 0) return {}
      const map = {}
      this.assignments[0].students.forEach((s, i) => {
        map[s.id] = this.jitter[i % this.jitter.length]
      })
      return map
    },

    // Trend lines for active students only (Mode B).
    // Each entry: { id, points, color }. Only students with 2+ data points get a line.
    studentPolylines() {
      if (this.mode !== 'assignments' || this.activeStudentIds.length === 0) return []
      return this.activeStudentIds.map(id => {
        const jitterX = this.studentJitterMap[id] || 0
        const pts = this.violins
          .map((v, i) => {
            const s = (v.students || []).find(s => s.id === id)
            return s && s.z !== null ? `${this.cx(i) + jitterX},${this.dotY(s.z)}` : null
          })
          .filter(Boolean)
        return { id, points: pts.join(' '), color: this.studentColors[id] || '#999999' }
      }).filter(line => line.points.split(' ').length >= 2)
    },
  },

  methods: {
    cx(i) {
      return this.leftOffset + i * this.colW + this.colW / 2
    },
    dotY(z) {
      return this.yCenter - z * this.yScale
    },

    // Builds an SVG path for a violin shape using Gaussian kernel density estimation.
    // The violin is wider where more students scored and narrower where fewer did.
    // bandwidth=0.7: deliberately high to produce a smooth, gentle shape suitable for teachers.
    // Lower bandwidth (e.g. 0.3) would show more detail but look spiky with small classes.
    violinPath(peers, centerX) {
      if (!peers || peers.length === 0) return ''

      const bandwidth = 1
      const steps = 40           // number of sample points along the y-axis

      // Clamp to actual data range to avoid thin spindles at the extremes.
      // The KDE's Gaussian tails produce near-zero but non-zero density all the way to ±3.5,
      // which creates long pointed spindles when data is clustered in the middle.
      // To restore spindles, replace the two lines below with: const yMin = -3.5; const yMax = 3.5
      const yMin = Math.min(...peers) - 1
      const yMax = Math.max(...peers) + 1
      // const yMin = -3.5;
      // const yMax = 3.5
      const maxHalfWidth = 18    // max width on either side of center, in SVG pixels

      // Gaussian kernel: measures how much each peer's score contributes at point x
      const kernel = (u) => Math.exp(-0.5 * u * u) / Math.sqrt(2 * Math.PI)
      const kde = (x) => peers.reduce((s, p) => s + kernel((x - p) / bandwidth), 0) / (peers.length * bandwidth)

      // No taper: whatever width the raw KDE has at yMin/yMax becomes the
      // width of a flat cap, not a value forced toward zero. Tapering the
      // density (linear or smoothstep) always ends in a literal point —
      // rounding the *slope* on the way down doesn't change that the width
      // still hits exactly 0 at the tip. What we actually want is a flat
      // top/bottom edge with only the two corners rounded off, like a
      // rounded rectangle end.
      const points = []
      for (let i = 0; i <= steps; i++) {
        const z = yMin + (yMax - yMin) * i / steps
        points.push({ z, d: kde(z) })
      }

      // Normalize so the widest point fills maxHalfWidth
      const maxD = Math.max(...points.map(p => p.d))
      if (maxD === 0) return ''

      // Build path: right side bottom-to-top, then left side bottom-to-top
      const rightSide = points.map(p => ({
        x: centerX + (p.d / maxD) * maxHalfWidth,
        y: this.dotY(p.z),
      }))
      const leftSide = points.map(p => ({
        x: centerX - (p.d / maxD) * maxHalfWidth,
        y: this.dotY(p.z),
      }))

      const bottomR = rightSide[0]
      const topR = rightSide[rightSide.length - 1]
      const bottomL = leftSide[0]
      const topL = leftSide[leftSide.length - 1]

      // Corner fillet radius, in px. Clamped to the flat edge's own
      // half-width so a very skinny end (where halfWidth < cornerRadius)
      // just rounds off entirely instead of producing a negative-length
      // flat segment.
      const cornerRadius = 3
      const rBottom = Math.min(cornerRadius, Math.abs(bottomR.x - centerX))
      const rTop = Math.min(cornerRadius, Math.abs(topR.x - centerX))

      const fmt = (p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`
      // Each fillet runs from a point ON the side curve (cornerRadius px
      // before the cap) to a point on the flat edge (cornerRadius px in
      // from the corner), with the sharp corner itself as the quadratic
      // control point — the standard cheap rounded-rectangle fillet.
      // The curve-side endpoint must be interpolated from the sampled
      // polyline: placing it at the corner's own x (as a naive inset
      // does) leaves it off the curve wherever the KDE width changes
      // near the cap, and the path visibly notches where it jumps back
      // to the first real sample.
      const pointOnSideAtY = (side, targetY) => {
        for (let i = 1; i < side.length; i++) {
          const a = side[i - 1]
          const b = side[i]
          if ((a.y - targetY) * (b.y - targetY) <= 0 && a.y !== b.y) {
            const t = (targetY - a.y) / (b.y - a.y)
            return { x: a.x + t * (b.x - a.x), y: targetY }
          }
        }
        return side[side.length - 1]
      }
      // SVG y grows downward: the bottom cap is at the largest y, so
      // "cornerRadius px into the body" means y - r at the bottom and
      // y + r at the top.
      const bottomRIn = pointOnSideAtY(rightSide, bottomR.y - rBottom)
      const bottomLIn = pointOnSideAtY(leftSide, bottomL.y - rBottom)
      const topRIn = pointOnSideAtY(rightSide, topR.y + rTop)
      const topLIn = pointOnSideAtY(leftSide, topL.y + rTop)
      const bottomRFlat = { x: bottomR.x - rBottom, y: bottomR.y }
      const bottomLFlat = { x: bottomL.x + rBottom, y: bottomL.y }
      const topRFlat = { x: topR.x - rTop, y: topR.y }
      const topLFlat = { x: topL.x + rTop, y: topL.y }

      // Only samples strictly between the two fillet zones; the fillets
      // replace the curve inside those zones.
      const bodySamples = (side) =>
        side.filter(p => p.y < bottomR.y - rBottom && p.y > topR.y + rTop)

      let d = `M${fmt(bottomRIn)} `
      d += `Q${fmt(bottomR)} ${fmt(bottomRFlat)} `
      d += `L${fmt(bottomLFlat)} `
      d += `Q${fmt(bottomL)} ${fmt(bottomLIn)} `
      d += bodySamples(leftSide).map(p => `L${fmt(p)}`).join(' ') + ' '
      d += `L${fmt(topLIn)} `
      d += `Q${fmt(topL)} ${fmt(topLFlat)} `
      d += `L${fmt(topRFlat)} `
      d += `Q${fmt(topR)} ${fmt(topRIn)} `
      d += bodySamples(rightSide).reverse().map(p => `L${fmt(p)}`).join(' ') + ' Z'

      return d
    },
  },
}
</script>
